/**
 * Pre-Audit Checklist Page
 *
 * Interactive pre-audit checklist page where consultants can conduct preliminary assessments
 * using the ChecklistGroup component with proper state management.
 *
 * Features:
 * - Audit information header
 * - Overall progress tracking
 * - Four checklist categories (Management, Premises, Hygiene, Production)
 * - Item state management (pending/completed/failed/not-applicable)
 * - Notes/comments per item
 * - Findings summary
 * - Save and submit functionality
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Check, FileText, Clock, XCircle, RotateCcw, Factory, Briefcase, Sparkles, Settings } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { ProgressBar } from '../../components/common/ProgressBar';
import { ChecklistGroup } from '../../components/checklist/ChecklistGroup';
import { useChecklistState } from '../../hooks/useChecklistState';
import type { ChecklistItem } from '../../hooks/useChecklistState';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

// Pre-audit checklist items organized by category
const PRE_AUDIT_CHECKLIST: Record<string, ChecklistItem[]> = {
  management: [
    {
      id: 'MS-COMP-001',
      label: 'Halal Policy Statement',
      description: 'Company has documented halal policy signed by top management',
      reference: 'MHMS 2020 - 4.2.1',
      required: true,
      category: 'management',
    },
    {
      id: 'MS-COMP-002',
      label: 'Halal Management Team',
      description: 'Halal management team appointed with clear roles and responsibilities',
      reference: 'MHMS 2020 - 4.2.2',
      required: true,
      category: 'management',
    },
    {
      id: 'MS-COMP-003',
      label: 'Internal Halal Committee',
      description: 'Internal halal committee established with regular meetings',
      reference: 'MHMS 2020 - 4.2.3',
      required: false,
      category: 'management',
    },
  ],
  premises: [
    {
      id: 'MS-PREM-001',
      label: 'Facility Layout',
      description: 'Facility layout prevents cross-contamination with non-halal products',
      reference: 'MHMS 2020 - 5.1.1',
      required: true,
      category: 'premises',
    },
    {
      id: 'MS-PREM-002',
      label: 'Storage Segregation',
      description: 'Separate storage for halal raw materials and finished products',
      reference: 'MHMS 2020 - 5.1.2',
      required: true,
      category: 'premises',
    },
  ],
  hygiene: [
    {
      id: 'MS-HYG-001',
      label: 'Cleaning Procedures',
      description: 'Documented cleaning and sanitation procedures for equipment',
      reference: 'MHMS 2020 - 5.2.1',
      required: true,
      category: 'hygiene',
    },
    {
      id: 'MS-HYG-002',
      label: 'Personal Hygiene',
      description: 'Personal hygiene requirements established and monitored',
      reference: 'MHMS 2020 - 5.2.2',
      required: true,
      category: 'hygiene',
    },
  ],
  production: [
    {
      id: 'MS-PROD-001',
      label: 'Product Traceability',
      description: 'Traceability system for raw materials to finished products',
      reference: 'MHMS 2020 - 6.1.1',
      required: true,
      category: 'production',
    },
    {
      id: 'MS-PROD-002',
      label: 'Halal Ingredients Verification',
      description: 'All ingredients verified as halal with supporting documentation',
      reference: 'MHMS 2020 - 6.1.2',
      required: true,
      category: 'production',
    },
  ],
};

export const PreAuditChecklistPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant', 'auditor']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const applicationId = id || 'app-001';

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');

  // Initialize checklist state with localStorage persistence
  const {
    state: checklistState,
    updateItemStatus,
    updateItemNotes,
    getProgress,
    resetAll,
  } = useChecklistState(
    `pre-audit-${applicationId}`,
    `pre-audit-${applicationId}`,
    applicationId
  );

  useEffect(() => {
    loadApplicationDetails();
  }, [applicationId]);

  const loadApplicationDetails = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getApplication(applicationId);
      if (response.success && response.data) {
        setApplication(response.data);
      }
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all checklist items
  const allItems = Object.values(PRE_AUDIT_CHECKLIST).flat();
  const overallProgress = getProgress(allItems);

  // Calculate findings summary
  const findingsSummary = {
    conformities: overallProgress.completed,
    minorFindings: overallProgress.failed,
    observations: overallProgress.notApplicable,
    pending: overallProgress.pending,
  };

  const handleSaveProgress = () => {
    setSaveMessage(t('consultant:messages.progressSaved'));
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleSubmitChecklist = () => {
    if (overallProgress.requiredPercentage < 100) {
      alert(`Please complete all required items before submitting. (${overallProgress.requiredCompleted}/${overallProgress.requiredTotal} completed)`);
      return;
    }

    if (window.confirm(t('consultant:preAudit.confirmSubmit') || 'Submit pre-audit checklist? This action cannot be undone.')) {
      // In a real app, this would submit to the API
      alert(t('consultant:messages.reportSubmitted'));
      navigate(`/consultant/clients/${applicationId}`);
    }
  };

  const handleResetChecklist = () => {
    if (window.confirm('Reset all checklist items? This action cannot be undone.')) {
      resetAll();
    }
  };

  const getProgressVariant = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('consultant:preAudit.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title'), href: '/consultant/clients' },
          { label: t('consultant:preAudit.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  if (!application) {
    return (
      <PageLayout
        pageTitle={t('consultant:preAudit.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title'), href: '/consultant/clients' },
          { label: t('consultant:preAudit.title') },
        ]}
      >
        <div className="text-center py-12">
          <p className="text-gray-500">Application not found</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={`${t('consultant:preAudit.title')} - ${application.companyName}`}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
        { label: t('consultant:clients.title'), href: '/consultant/clients' },
        { label: application.companyName, href: `/consultant/clients/${applicationId}` },
        { label: t('consultant:preAudit.title') },
      ]}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleResetChecklist} icon={<RotateCcw className="w-4 h-4" />}>
            Reset
          </Button>
          <Button variant="outline" onClick={handleSaveProgress} icon={<Save className="w-4 h-4" />}>
            {t('consultant:actions.saveProgress')}
          </Button>
          <Button onClick={handleSubmitChecklist} icon={<Check className="w-4 h-4" />}>
            {t('consultant:actions.submitReport')}
          </Button>
        </div>
      }
    >
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {saveMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Checklist */}
        <div className="lg:col-span-2 space-y-6">
          {/* Audit Information Header */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Audit Information</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Company</p>
                  <p className="mt-1">{application.companyName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Application Type</p>
                  <p className="mt-1">{application.type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Scheduled Date</p>
                  <p className="mt-1">{formatDate(new Date().toISOString())}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Consultant</p>
                  <p className="mt-1">Current User</p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Checklist Categories */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('consultant:preAudit.checklist')}</h2>

            {/* Management Responsibility */}
            <ChecklistGroup
              title="1. Management Responsibility"
              description="Top management commitment to halal assurance system"
              icon={<Briefcase className="w-5 h-5" />}
              items={PRE_AUDIT_CHECKLIST.management}
              itemStates={checklistState.items}
              onStatusChange={updateItemStatus}
              onNotesChange={updateItemNotes}
              showProgress={true}
            />

            {/* Premises & Facilities */}
            <ChecklistGroup
              title="2. Premises & Facilities"
              description="Facility layout and storage requirements"
              icon={<Factory className="w-5 h-5" />}
              items={PRE_AUDIT_CHECKLIST.premises}
              itemStates={checklistState.items}
              onStatusChange={updateItemStatus}
              onNotesChange={updateItemNotes}
              showProgress={true}
            />

            {/* Hygiene & Sanitation */}
            <ChecklistGroup
              title="3. Hygiene & Sanitation"
              description="Cleaning procedures and personal hygiene"
              icon={<Sparkles className="w-5 h-5" />}
              items={PRE_AUDIT_CHECKLIST.hygiene}
              itemStates={checklistState.items}
              onStatusChange={updateItemStatus}
              onNotesChange={updateItemNotes}
              showProgress={true}
            />

            {/* Production & Quality Control */}
            <ChecklistGroup
              title="4. Production & Quality Control"
              description="Traceability and ingredient verification"
              icon={<Settings className="w-5 h-5" />}
              items={PRE_AUDIT_CHECKLIST.production}
              itemStates={checklistState.items}
              onStatusChange={updateItemStatus}
              onNotesChange={updateItemNotes}
              showProgress={true}
            />
          </div>
        </div>

        {/* Sidebar - Progress & Summary */}
        <div className="space-y-6">
          {/* Overall Progress */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('consultant:preAudit.overallProgress')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Completion</span>
                    <span className="text-2xl font-bold text-gray-900">{overallProgress.percentage}%</span>
                  </div>
                  <ProgressBar
                    value={overallProgress.percentage}
                    variant={getProgressVariant(overallProgress.percentage)}
                  />
                </div>

                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total Items</span>
                    <span className="font-medium">{overallProgress.total}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Completed</span>
                    <span className="font-medium text-green-600">{overallProgress.completed}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Pending</span>
                    <span className="font-medium text-gray-600">{overallProgress.pending}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Failed</span>
                    <span className="font-medium text-red-600">{overallProgress.failed}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Not Applicable</span>
                    <span className="font-medium text-gray-400">{overallProgress.notApplicable}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Required Items</span>
                    <Badge variant={overallProgress.requiredPercentage === 100 ? 'success' : 'warning'}>
                      {overallProgress.requiredCompleted}/{overallProgress.requiredTotal}
                    </Badge>
                  </div>
                  <ProgressBar
                    value={overallProgress.requiredPercentage}
                    variant={overallProgress.requiredPercentage === 100 ? 'success' : 'warning'}
                    className="mt-2"
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Findings Summary */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('consultant:preAudit.findingsSummary')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Check className="w-6 h-6 text-success-600" />
                    <span className="text-sm font-medium text-gray-700">{t('consultant:preAudit.conformities')}</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">{findingsSummary.conformities}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-6 h-6 text-error-600" />
                    <span className="text-sm font-medium text-gray-700">{t('consultant:preAudit.minorFindings')}</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">{findingsSummary.minorFindings}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-6 h-6 text-neutral-600" />
                    <span className="text-sm font-medium text-gray-700">{t('consultant:preAudit.observations')}</span>
                  </div>
                  <span className="text-xl font-bold text-gray-600">{findingsSummary.observations}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="w-6 h-6 text-warning-600" />
                    <span className="text-sm font-medium text-gray-700">{t('consultant:preAudit.itemStatus.pending')}</span>
                  </div>
                  <span className="text-xl font-bold text-orange-600">{findingsSummary.pending}</span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Readiness Assessment */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('consultant:preAudit.readinessScore')}</h3>
            </CardHeader>
            <CardBody>
              <div className="text-center py-4">
                <div className="text-5xl font-bold mb-2"
                  style={{
                    color: overallProgress.percentage >= 80 ? '#10b981' :
                           overallProgress.percentage >= 60 ? '#f59e0b' : '#ef4444'
                  }}>
                  {overallProgress.percentage}%
                </div>
                <Badge
                  variant={
                    overallProgress.percentage >= 80 ? 'success' :
                    overallProgress.percentage >= 60 ? 'warning' : 'error'
                  }
                  size="lg"
                >
                  {overallProgress.percentage >= 80 ? t('consultant:status.readyForAudit') :
                   overallProgress.percentage >= 60 ? 'Approaching Readiness' :
                   t('consultant:status.needsImprovement')}
                </Badge>
              </div>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <Button fullWidth onClick={handleSaveProgress} variant="outline" icon={<Save className="w-4 h-4" />}>
                  {t('consultant:actions.saveProgress')}
                </Button>
                <Button
                  fullWidth
                  onClick={handleSubmitChecklist}
                  disabled={overallProgress.requiredPercentage < 100}
                  icon={<Check className="w-4 h-4" />}
                >
                  {t('consultant:actions.submitReport')}
                </Button>
                <Button fullWidth variant="outline" onClick={() => navigate(`/consultant/clients/${applicationId}`)}>
                  ← Back to Client
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default PreAuditChecklistPage;

